import React, { useMemo } from 'react'
import Modal from '~/prix/react/components/modal'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import useAction from '~/prix/react/hooks/action'
import actionDefinitions from '../actionDefinitions'
import Handle from '~/prix/react/components/handle'
import { GenericObject } from '~/prix/types/type'
import styled from 'styled-components'
import theme, { institutionalColors, organizationalColors } from '~/design'
import SearchField from '~/prix/react/components/form/inputs/searchField'
import { formatAsBrNumber } from '~/prix'
import pluralize from '~/prix/utils/pluralize'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { ModalInformation } from '../general/detailedInformation/modalInformation'
import { buildClusterListInput, isVariableLayer } from './clusterListInput.util'
import {
  buildClusterListItems,
  formatClusterListValue,
  getClusterListColumns,
  getClusterListGridTemplate,
} from './clusterListTable.util'
import { resolveMarkerCoordinates } from '../general/detailedInformation/markerCoordinates.util'

export interface SelectedCluster {
  layer: Layer
}

export interface ClusterListProps {
  selectedCluster: SelectedCluster
  onClose: () => void
}

const ModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border-radius: 20px;
  background: ${institutionalColors.brand97};
  box-shadow: 0 24px 64px rgba(12, 35, 64, 0.18);
`

const Wrapper = styled.div<{ $isLoading: boolean }>`
  display: flex;
  flex: 1;
  min-height: 0;
  justify-content: stretch;
  align-items: ${({ $isLoading }) => ($isLoading ? 'center' : 'flex-start')};
  overflow: hidden;
  background:
    radial-gradient(circle at top right, rgba(20, 173, 204, 0.08), transparent 24%),
    linear-gradient(180deg, rgba(15, 67, 138, 0.05) 0%, #ffffff 22%);
`

const SubTitle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.01em;
`

const FixedHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 1;
`

const SearchPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 24px 14px;
  background: linear-gradient(180deg, #fff 0%, ${institutionalColors.neutral97} 100%);
  border-bottom: 1px solid ${institutionalColors.neutral90};

  @media screen and (max-width: 768px) {
    padding: 16px;
  }
`

const SearchMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const SearchHint = styled.span`
  color: ${institutionalColors.neutral45};
  font-size: 0.82rem;
`

const SearchCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.42rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${institutionalColors.neutral90};
  background: #fff;
  color: ${institutionalColors.brand20};
  font-size: 0.8rem;
  font-weight: 700;
`

const ScrollableList = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #fff;
`

const BodyWrapper = styled.div`
  background-color: rgba(255, 255, 255, 0.98);
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

const CardEntitiesListTitle = styled.div`
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(135deg, ${institutionalColors.brand20} 0%, ${institutionalColors.brand40} 58%, ${organizationalColors.aquaBlue30} 100%);
  padding: 24px 24px 20px;
  width: 100%;
  min-height: 120px;
  gap: 12px;
  justify-content: center;
  align-items: flex-start;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);

  @media screen and (max-width: 768px) {
    min-height: 92px;
    padding: 20px 16px 16px;
  }
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  min-width: 0;

  h3 {
    color: #fff;
    font-weight: 700;
    line-height: 1.1;
    font-size: 1.75rem;
    margin: 0;
    padding: 0;

    @media screen and (max-width: 768px) {
      font-size: 1.35rem;
    }
  }
`

const HeaderEyebrow = styled.span`
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const HeaderContext = styled.span`
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.9rem;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ListHeaderRow = styled.div<{ $gridTemplateColumns: string }>`
  display: grid;
  grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
  gap: 12px;
  align-items: center;
  padding: 0 24px 12px;
  border-bottom: 1px solid ${institutionalColors.neutral90};
  flex-shrink: 0;
  background-color: ${institutionalColors.neutral97};

  @media screen and (max-width: 768px) {
    display: none;
  }
`

const ListHeaderColumn = styled.div`
  display: block;
  min-width: 0;
  color: ${institutionalColors.neutral60};
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`

const ListRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media screen and (max-width: 768px) {
    min-height: auto;
  }
`

const RecordListButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  color: ${institutionalColors.neutral30};
  cursor: pointer;
  border-bottom: 1px solid ${institutionalColors.neutral90};
  padding: 0;
  transition:
    background-color ${theme.transitionDurationMs}ms ${theme.transitionTimingFunction},
    transform ${theme.transitionDurationMs}ms ${theme.transitionTimingFunction};

  &:hover {
    background:
      linear-gradient(90deg, rgba(15, 67, 138, 0.04) 0%, rgba(20, 173, 204, 0.08) 100%);
  }

  &:focus-visible {
    outline: 2px solid ${institutionalColors.brand60};
    outline-offset: -2px;
  }

  &:last-child {
    border-bottom: none;
  }

  @media screen and (max-width: 768px) {
    border: none;
    border-bottom: 1px solid ${institutionalColors.neutral90};
  }
`

const RecordContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`

const ListItem = styled.div<{ $gridTemplateColumns: string }>`
  position: relative;
  display: grid;
  grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
  gap: 12px;
  align-items: center;
  width: 100%;
  max-width: 100%;
  padding: 16px 18px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 14px 16px;
  }
`

const ListItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: ${institutionalColors.brand97};
  color: ${institutionalColors.brand30};
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    position: absolute;
    right: 16px;
    top: 16px;
  }
`

const ListData = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  gap: 4px;

  @media screen and (max-width: 768px) {
    padding-right: 36px;
  }
`

const ListLabel = styled.span`
  display: none;
  color: ${institutionalColors.neutral60};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media screen and (max-width: 768px) {
    display: inline-flex;
  }
`

const PrimaryValue = styled.span`
  color: ${institutionalColors.brand20};
  font-size: 0.98rem;
  font-weight: 700;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Value = styled.span`
  color: ${institutionalColors.neutral20};
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EmptyState = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1.5rem;
  color: ${institutionalColors.neutral45};
  font-size: 0.95rem;
  text-align: center;
`

const writeEntitiesList = (
  data: GenericObject,
  gridTemplateColumns: string,
  columns: ReturnType<typeof getClusterListColumns>,
) => {
  return (
    <RecordContentStack>
      <ListItem $gridTemplateColumns={gridTemplateColumns}>
        {columns.map((column, index) => {
          const formattedValue = formatClusterListValue(data, column)
          return (
            <ListData key={column.key}>
              <ListLabel>{column.label}</ListLabel>
              {index === 0 ? (
                <PrimaryValue>{formattedValue}</PrimaryValue>
              ) : (
                <Value>{formattedValue}</Value>
              )}
            </ListData>
          )
        })}
        <ListItemIcon>
          <FontAwesomeIcon icon={faChevronRight} />
        </ListItemIcon>
      </ListItem>
    </RecordContentStack>
  )
}

export default function ClusterList({ selectedCluster, onClose }: ClusterListProps) {
  const { layer } = selectedCluster
  const [modalInformationState, setModalInformationState] = React.useState<GenericObject | null>(
    null,
  )
  const [recordsSearch, setRecordsSearch] = React.useState('')

  if (!isVariableLayer(layer)) {
    throw new Error('Cluster list is available only for variable layers')
  }

  const actionDefinition = useMemo(() => {
    const actionDefinition = actionDefinitions.find(
      actionDefinition => actionDefinition.key === layer.variable.actionDefinitionKey,
    )
    if (!actionDefinition) {
      throw new Error(`Action definition not found for variable ${layer.variable.key}`)
    }

    return actionDefinition
  }, [layer])

  const input = useMemo(
    () => buildClusterListInput(layer),
    [layer],
  )

  const { result, isLoading, error } = useAction(actionDefinition, input)
  const items: GenericObject[] = result?.items || []
  const columns = useMemo(() => getClusterListColumns(layer), [layer])
  const gridTemplateColumns = useMemo(() => getClusterListGridTemplate(columns), [columns])

  const processedItems = useMemo(() => {
    if (!items?.length) return []

    return buildClusterListItems(items, columns)
  }, [columns, items])

  const headerContext = useMemo(() => {
    const parts = [layer.variable.title, layer.space?.delimitationTitle].filter(Boolean)
    return parts.join(' em ')
  }, [layer.space?.delimitationTitle, layer.variable.title])

  const [filteredItems, stats]: [
    { searchableText: string }[],
    { totalItems: number; totalItemsFormatted: string } | null,
  ] = useMemo(() => {
    if (!processedItems.length) return [[], null]

    let result = processedItems

    if (recordsSearch) {
      const searchTerm = recordsSearch.toLowerCase()
      result = result.filter(item => item.searchableText.includes(searchTerm))
    }

    const stats = {
      totalItems: result.length,
      totalItemsFormatted: formatAsBrNumber(result.length),
    }

    return [result, stats]
  }, [processedItems, recordsSearch])

  const modalMarkerCoordinates = useMemo(
    () => resolveMarkerCoordinates(modalInformationState),
    [modalInformationState],
  )

  if (!layer.variable.basicSelect) {
    throw new Error('Basic select is required for cluster list')
  }
  if (!layer.variable.shape) {
    throw new Error('Shape is required for cluster list')
  }

  return (
    <React.Fragment>
      <Modal
        onClose={onClose}
        isOpen
        baseWidth='90vw'
        baseHeight='80vh'
        styles={{
          content: {
            maxWidth: '100%',
            background: 'transparent',
            boxShadow: 'none',
            borderRadius: 0,
            padding: 0,
            overflow: 'hidden',
          },
        }}
      >
        <ModalLayout>
          <CardEntitiesListTitle>
            <TitleContainer>
              <HeaderEyebrow>Abrir registros</HeaderEyebrow>
              <h3>Lista de registros</h3>
              {headerContext ? <HeaderContext>{headerContext}</HeaderContext> : null}
              {stats && (
                <SubTitle>
                  {stats.totalItemsFormatted}{' '}
                  {pluralize(stats.totalItems, 'registro encontrado', 'registros encontrados')}
                </SubTitle>
              )}
            </TitleContainer>
          </CardEntitiesListTitle>
          <Wrapper $isLoading={isLoading}>
            <Handle isLoading={isLoading} error={error}>
              <BodyWrapper>
                <FixedHeader>
                  <SearchPanel>
                    <SearchField value={recordsSearch || ''} onChange={setRecordsSearch} />
                    <SearchMetaRow>
                      <SearchHint>Busque por nome, identificador ou qualquer detalhe do registro.</SearchHint>
                      {stats ? (
                        <SearchCount>
                          {stats.totalItemsFormatted} {pluralize(stats.totalItems, 'registro', 'registros')}
                        </SearchCount>
                      ) : null}
                    </SearchMetaRow>
                  </SearchPanel>
                  <ListHeaderRow $gridTemplateColumns={gridTemplateColumns}>
                    {columns.map(column => (
                      <ListHeaderColumn key={column.key}>{column.label}</ListHeaderColumn>
                    ))}
                    <ListHeaderColumn />
                  </ListHeaderRow>
                </FixedHeader>
                <ScrollableList>
                  {filteredItems.length > 0 ? (
                    filteredItems.map((currentRecord, index) => (
                      <RecordListButton
                        key={index}
                        onClick={event => {
                          event.preventDefault()
                          setModalInformationState(currentRecord)
                        }}
                      >
                        <ListRow>{writeEntitiesList(currentRecord, gridTemplateColumns, columns)}</ListRow>
                      </RecordListButton>
                    ))
                  ) : (
                    <EmptyState>Nenhum registro encontrado para esse filtro.</EmptyState>
                  )}
                </ScrollableList>
              </BodyWrapper>
            </Handle>
          </Wrapper>
        </ModalLayout>
      </Modal>

      {/* Modal com detalhes do item */}
      <Modal
        isOpen={!!modalInformationState}
        onClose={() => setModalInformationState(null)}
        baseWidth='90vw'
        baseHeight='80vh'
      >
        {modalInformationState && (
          <ModalInformation
            isOpen
            onClose={() => setModalInformationState(null)}
            item={modalInformationState}
            isLoading={false}
            detailKey={layer.variable.key}
            layer={layer}
            coordinates={modalMarkerCoordinates ?? undefined}
          />
        )}
      </Modal>
    </React.Fragment>
  )
}
